import type { TaskParcel } from "@/hooks/useTaskRelatedFrames";
import { useClickWithoutDrag } from "@/hooks/useClickWithoutDrag";
import { useCoverageBetaStore } from "@/store/useCoverageBetaStore";
import { getDetectionBoxGeometry } from "@/utils/detection-box-geometry";
import { isLikelyCeilingGuess } from "@/utils/hidden-sam2-guesses";
import {
  quadIou,
  PROPAGATED_IOU_MATCH,
} from "@/utils/merge-propagated-annotations";
import { cn } from "@/utils/utils";
import { useAtlasProducts } from "@labeling-tools/api/hooks";
import { keyBy, uniq } from "lodash";
import { useMemo, type CSSProperties } from "react";
import { OrientedBoxOverlay } from "../OrientedBoxOverlay";

type Size = { width: number; height: number };

/** One known area as drawn on a context frame. */
export interface AreaItem {
  key: string;
  /** 4 corners, normalized 0-1 to the context frame. */
  points: number[][];
  label: string;
  /** The area's products — what hovering it points at on the planogram. */
  productIds: string[];
  hasProducts: boolean;
  /** A labeler's area, vs SAM2's guess (which shows its score). */
  isHuman: boolean;
  score: number;
  /**
   * A SAM 2 guess the labeler UI leaves out (see isLikelyCeilingGuess). Only
   * the admin view gets these; it draws them faded and tagged.
   */
  hiddenFromLabelers: boolean;
}

/**
 * The areas known for `frame` (lebron mvp.propagation) — a labeler's once the
 * parcel has been labeled, else SAM2's guesses — each tagged with its product.
 * Several labeled tasks can cover the same spot; the highest-scoring one per
 * spot is kept. Product details are fetched once for all of `frames`, so
 * scrubbing doesn't refetch.
 */
type WithAnnotations = Pick<TaskParcel, "annotations">;

/**
 * Violet is SAM 2's guess, green a labeler's product, gray a labeler's
 * "No match" — the same meaning as on the task's own image.
 */
export const propagatedItemStyle = (
  item: Pick<AreaItem, "isHuman" | "hasProducts">,
): { box: string; label: string } => {
  if (!item.isHuman)
    return {
      box: "stroke-violet-400 fill-violet-400/30",
      label: "bg-violet-600/90",
    };
  if (item.hasProducts)
    return {
      box: "stroke-green-400 fill-green-400/30",
      label: "bg-green-600/90",
    };
  return { box: "stroke-gray-500 fill-gray-500/30", label: "bg-gray-600/90" };
};

export const usePropagatedAreaItems = (
  frames: WithAnnotations[] | null,
  frame: WithAnnotations | null,
): AreaItem[] => {
  const productIds = useMemo(
    () =>
      uniq(
        (frames ?? []).flatMap((f) =>
          f.annotations.flatMap((a) => a.product_ids),
        ),
      ),
    [frames],
  );
  const { products } = useAtlasProducts(productIds);
  const productsById = keyBy(
    products.filter((p) => !!p?.id),
    "id",
  );

  const kept: AreaItem[] = [];
  [...(frame?.annotations ?? [])]
    .sort((a, b) => b.score - a.score)
    .forEach((a) => {
      if (kept.some((k) => quadIou(k.points, a.points) >= PROPAGATED_IOU_MATCH))
        return;
      const product = productsById[a.product_ids[0]];
      kept.push({
        key: `${a.source_task_id}:${a.area_id}`,
        points: a.points,
        label: product
          ? product.description || product.identifier || "product"
          : a.product_ids.length
            ? "product"
            : // A labeled area with no product can only be "No product match"
              // (submit needs every box decided); SAM 2 carries it as is.
              "No match",
        productIds: a.product_ids,
        hasProducts: a.product_ids.length > 0,
        isHuman: a.source === "HUMAN",
        score: a.score,
        hiddenFromLabelers: isLikelyCeilingGuess(a),
      });
    });
  return kept;
};

/**
 * Where an `object-contain` image of `frame` size sits inside a box shaped
 * like `container`, as percentages of that box. Identity when the aspect
 * ratios match (same camera).
 */
const containRect = (container: Size, frame: Size): CSSProperties => {
  const containerAspect = container.width / container.height;
  const frameAspect = frame.width / frame.height;
  if (frameAspect > containerAspect) {
    const height = (containerAspect / frameAspect) * 100;
    return {
      left: 0,
      width: "100%",
      top: `${(100 - height) / 2}%`,
      height: `${height}%`,
    };
  }
  const width = (frameAspect / containerAspect) * 100;
  return {
    top: 0,
    height: "100%",
    left: `${(100 - width) / 2}%`,
    width: `${width}%`,
  };
};

interface Props {
  items: AreaItem[];
  /** The task frame's natural size — the shape of the box we draw in. */
  containerSize: Size | null;
  /** The context frame's natural size. */
  frameSize: Size | null;
}

/**
 * Known areas on a context frame / similar view, tagged with their product.
 * Not editable — labeling stays on the task's primary image — but hovering an
 * area lights its products up on the planogram and shelf images, and clicking
 * keeps its product highlighted and frames it on the planogram (when that's
 * the shown view), so the labeler can localize.
 */
export const PropagatedAreaOverlay = (props: Props) => {
  const { items, containerSize, frameSize } = props;
  const setNeighbourProductIds = useCoverageBetaStore(
    (s) => s.setNeighbourProductIds,
  );
  const setActiveProductId = useCoverageBetaStore((s) => s.setActiveProductId);
  const requestPlanogramFocus = useCoverageBetaStore(
    (s) => s.requestPlanogramFocus,
  );
  const { onPointerDown, isClick } = useClickWithoutDrag();
  const rect =
    containerSize && frameSize
      ? containRect(containerSize, frameSize)
      : { inset: 0 };

  return (
    <div className="absolute pointer-events-none" style={rect}>
      {items.map((item) => {
        const { style, corners } = getDetectionBoxGeometry(item.points);
        const { hasProducts, productIds } = item;
        return (
          <div
            key={item.key}
            className={cn(
              "absolute",
              hasProducts && "pointer-events-auto cursor-pointer",
              item.hiddenFromLabelers && "opacity-50",
            )}
            style={style}
            onMouseEnter={() => setNeighbourProductIds(productIds)}
            onMouseLeave={() => setNeighbourProductIds([])}
            onPointerDown={onPointerDown}
            onClick={(e) => {
              const productId = productIds[0];
              if (!productId || !isClick(e)) return;
              setActiveProductId(productId);
              requestPlanogramFocus(productId);
            }}
          >
            <OrientedBoxOverlay
              corners={corners}
              className={propagatedItemStyle(item).box}
              strokeWidth={2}
              dashed={item.hiddenFromLabelers}
            />
            <span
              className={cn(
                "absolute bottom-full left-0 mb-0.5 max-w-[240px] truncate whitespace-nowrap rounded px-1 text-[10px] font-semibold text-white",
                propagatedItemStyle(item).label,
              )}
            >
              {item.label}
              {!item.isHuman && ` · ${item.score.toFixed(2)}`}
              {item.hiddenFromLabelers && " · hidden from labelers"}
            </span>
          </div>
        );
      })}
    </div>
  );
};
